import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/**
 * DocumentsBoard — #62 next-phase Documents (cmdr/UXeng, 2026-08-24). Phase-3 SCOPED per build-plan
 * §6.3: upload/list/download/folders within a tenant. ZERO sharing/ACL surface — 'Shared With' is
 * OMITTED entirely (not even a static label; RBAC doesn't exist, and presence-without-function on a
 * privacy surface is the worst place for it, cmdr 2026-08-24). For the same reason a "Share" action is
 * ALSO cut: there is no permission model to share INTO, so a Share button would be the identical
 * presence-without-function defect on the actions column instead of a grid column. Actions are
 * Download / Delete only.
 */
interface Folder { id: string; name: string; createdAt: string }
interface Doc { id: string; folderId: string | null; name: string; mimeType: string; size: number; uploadedAt: string }
interface Pending { tempId: string; name: string; size: number; error?: string }

function mimeLabel(mimeType: string): string {
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.includes('word')) return 'Word';
  if (mimeType.includes('sheet') || mimeType === 'application/vnd.ms-excel') return 'Excel';
  if (mimeType === 'text/csv') return 'CSV';
  if (mimeType === 'text/plain') return 'Text';
  if (mimeType.startsWith('image/')) return 'Image';
  return mimeType;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const DocumentsBoard: React.FC = () => {
  const { t } = useTranslation('common');
  // X-1 via useListFetch — this board was one of the TWO loading-forever variants (D-D3): a failed
  // load left null state and the loading string rendered until the heat-death of the session.
  const [rk, setRk] = useState(0);
  const list = useListFetch<{ folders: Folder[]; documents: Doc[] }>('/api/documents', { refreshKey: rk });
  const folders = list.data?.folders ?? null;
  const documents = list.data?.documents ?? null;
  const setFolders = (fn: (fs: Folder[] | null) => Folder[]) =>
    list.setData((d) => ({ folders: fn(d?.folders ?? null), documents: d?.documents ?? [] }));
  const setDocuments = (fn: (ds: Doc[] | null) => Doc[]) =>
    list.setData((d) => ({ folders: d?.folders ?? [], documents: fn(d?.documents ?? null) }));
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = () => setRk((k) => k + 1);

  const currentFolder = currentFolderId ? (folders ?? []).find((f) => f.id === currentFolderId) ?? null : null;
  const visibleDocs = (documents ?? []).filter((d) => d.folderId === currentFolderId);

  const uploadFile = async (file: File) => {
    const tempId = `pending-${Date.now()}-${Math.random()}`;
    setPending((p) => [...p, { tempId, name: file.name, size: file.size }]);
    const fd = new FormData();
    fd.append('file', file);
    if (currentFolderId) fd.append('folderId', currentFolderId);
    try {
      const res = await fetch('/api/documents', { method: 'POST', body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || t('documentsBoard.uploadFailed'));
      setDocuments((docs) => [body, ...(docs ?? [])]);
      setPending((p) => p.filter((u) => u.tempId !== tempId));
    } catch (e: any) {
      setPending((p) => p.map((u) => u.tempId === tempId ? { ...u, error: e.message || t('documentsBoard.uploadFailed') } : u));
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void uploadFile(file);
  };

  const createFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = folderName.trim();
    if (!name) return;
    const res = await fetch('/api/documents/folders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
    if (res.ok) {
      const folder = await res.json();
      setFolders((fs) => [...(fs ?? []), folder]);
      setFolderName('');
      setCreatingFolder(false);
    }
  };

  // ConfirmDialog, never window.confirm (P7 / D-D2). Confirm-first is correct here — delete IS the
  // whole record and there is no undo for bytes — it was only the dialog that was wrong.
  const [confirmingDelete, setConfirmingDelete] = useState<Doc | null>(null);
  const deleteDoc = async (doc: Doc) => {
    setConfirmingDelete(null);
    setDocuments((docs) => (docs ?? []).filter((d) => d.id !== doc.id)); // optimistic
    const res = await fetch(`/api/documents/${doc.id}`, { method: 'DELETE' });
    if (!res.ok) { load(); } // revert to server truth on failure
  };

  if (list.status === 'error') {
    return <ListStateShell status="error" testIdBase="documents-board" onRetry={list.retry}>{null}</ListStateShell>;
  }
  if (!folders || !documents) return <div className="p-6 text-sm text-text-muted" data-testid="documents-loading">{t('documentsBoard.loading')}</div>;

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-label-caps uppercase text-text-muted" data-testid="documents-breadcrumb">
          {currentFolder ? (
            <>
              <button type="button" data-testid="documents-breadcrumb-root" onClick={() => setCurrentFolderId(null)} className="hover:underline">{t('documentsBoard.title')}</button>
              {' / '}{currentFolder.name}
            </>
          ) : t('documentsBoard.title')}
        </div>
        <div className="ms-auto flex items-center gap-2">
          <button type="button" data-testid="documents-new-folder" onClick={() => setCreatingFolder((v) => !v)}
            className="h-9 px-3 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong">
            {t('documentsBoard.newFolder')}
          </button>
          <button type="button" data-testid="documents-upload" onClick={() => fileInputRef.current?.click()}
            className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold">
            {t('documentsBoard.upload')}
          </button>
          <input ref={fileInputRef} type="file" data-testid="documents-upload-input" className="hidden" onChange={onFilePicked} />
        </div>
      </div>

      {creatingFolder && (
        <form onSubmit={createFolder} className="flex items-center gap-2 mb-3">
          <input data-testid="documents-folder-name-input" value={folderName} onChange={(e) => setFolderName(e.target.value)}
            placeholder={t('documentsBoard.folderNamePlaceholder')} dir="auto" className="h-9 px-3 rounded-lg border border-border-neutral text-sm flex-1 max-w-xs" />
          <button type="submit" data-testid="documents-folder-create-submit" className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold">{t('documentsBoard.create')}</button>
        </form>
      )}

      {!currentFolder && folders.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="documents-folder-list">
          {folders.map((f) => (
            <button key={f.id} type="button" data-testid="documents-folder-chip" onClick={() => setCurrentFolderId(f.id)}
              className="h-9 px-3 rounded-lg bg-canvas border border-border-neutral text-sm text-text-strong">
              📁 {f.name}
            </button>
          ))}
        </div>
      )}

      <div className="bg-surface border border-border-neutral rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-neutral text-start">
              <th className="p-cell-y px-cell-x text-start text-xs font-semibold text-text-muted uppercase">{t('documentsBoard.columns.name')}</th>
              <th className="p-cell-y px-cell-x text-start text-xs font-semibold text-text-muted uppercase">{t('documentsBoard.columns.type')}</th>
              <th className="p-cell-y px-cell-x text-start text-xs font-semibold text-text-muted uppercase">{t('documentsBoard.columns.size')}</th>
              <th className="p-cell-y px-cell-x text-start text-xs font-semibold text-text-muted uppercase">{t('documentsBoard.columns.uploaded')}</th>
              <th className="p-cell-y px-cell-x text-start text-xs font-semibold text-text-muted uppercase">{t('documentsBoard.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((p) => (
              <tr key={p.tempId} data-testid="documents-pending-row" className="border-b border-border-neutral">
                <td className="p-cell-y px-cell-x text-text-strong">{p.name}</td>
                <td className="p-cell-y px-cell-x text-text-muted">—</td>
                <td className="p-cell-y px-cell-x text-text-muted" dir="ltr">{formatSize(p.size)}</td>
                <td className="p-cell-y px-cell-x" colSpan={2}>
                  {p.error ? (
                    <span data-testid="documents-upload-error" className="text-opt-rose">{p.error}</span>
                  ) : (
                    <span data-testid="documents-upload-pending" className="text-text-muted">{t('documentsBoard.uploading')}</span>
                  )}
                </td>
              </tr>
            ))}
            {visibleDocs.length === 0 && pending.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-text-muted" data-testid="documents-empty">{t('documentsBoard.empty')}</td></tr>
            ) : visibleDocs.map((doc) => (
              <tr key={doc.id} data-testid="documents-row" className="border-b border-border-neutral last:border-0 hover:bg-canvas">
                <td className="p-cell-y px-cell-x text-text-strong truncate">{doc.name}</td>
                <td className="p-cell-y px-cell-x text-text-muted">{mimeLabel(doc.mimeType)}</td>
                <td className="p-cell-y px-cell-x text-text-muted" dir="ltr">{formatSize(doc.size)}</td>
                <td className="p-cell-y px-cell-x text-text-muted" dir="ltr">{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                <td className="p-cell-y px-cell-x">
                  <div className="flex items-center gap-2">
                    <a data-testid="documents-download" href={`/api/documents/${doc.id}/download`} className="text-primary text-xs font-semibold hover:underline">{t('documentsBoard.download')}</a>
                    <button type="button" data-testid="documents-delete" onClick={() => setConfirmingDelete(doc)} className="text-opt-rose text-xs font-semibold hover:underline">{t('documentsBoard.delete')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Virus-scan is explicitly DEFERRED (cmdr, 2026-08-24) — a PROD go-live gate, named here rather
          than silently absent. */}
      <div className="text-[11px] text-text-muted mt-2" data-testid="documents-scan-notice">{t('documentsBoard.scanDeferredNotice')}</div>

      {confirmingDelete && (
        <ConfirmDialog destructive
          message={t('documentsBoard.deleteConfirm', { name: confirmingDelete.name })}
          onConfirm={() => void deleteDoc(confirmingDelete)}
          onClose={() => setConfirmingDelete(null)} />
      )}
    </div>
  );
};
